import {
  freshRequest,
  requestSchema,
  responseSchema,
  type NyxRequest,
  type NyxResponse,
} from '@atum-remote/nyx-link-protocol';
import { z } from 'zod';
import { browserTabProtocols } from './lib/browser-tab.js';
import { bindSessionActivity } from './lib/session-activity.js';

export interface NyxConnection {
  request(request: NyxRequest): Promise<NyxResponse>;
  isOpen(): boolean;
  close(): Promise<void>;
}
export async function browserNyxConnection(
  apiBase: string,
  lease: unknown,
  closeLease: (id: string) => Promise<unknown>,
): Promise<NyxConnection> {
  const session = z
    .object({
      sessionId: z.uuid(),
      key: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
      expiresAt: z.number().int(),
    })
    .parse(lease);
  const url = new URL(`${apiBase}/browser/sessions/${session.sessionId}/socket`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  const socket = new WebSocket(url, browserTabProtocols());
  let pending: {
    id: string;
    resolve: (value: NyxResponse) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  let ready = false;
  const unbindActivity = bindSessionActivity(socket, () => ready);
  let resolveReady: () => void = () => undefined;
  let rejectReady: (error: Error) => void = () => undefined;
  const opened = new Promise<void>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  const fail = () => {
    unbindActivity();
    ready = false;
    const error = new Error('Nyx connection closed. Reconnect to view task status.');
    rejectReady(error);
    if (pending) {
      clearTimeout(pending.timer);
      pending.reject(error);
      pending = null;
    }
  };
  const startup = setTimeout(() => {
    fail();
    socket.close();
  }, 12000);
  socket.onopen = () => socket.send(JSON.stringify({ type: 'attach', key: session.key }));
  socket.onerror = fail;
  socket.onclose = fail;
  socket.onmessage = (message) => {
    try {
      if (
        typeof message.data !== 'string' ||
        new TextEncoder().encode(message.data).length > 64 * 1024
      )
        throw new Error('Invalid frame.');
      const value: unknown = JSON.parse(message.data);
      const activity = z
        .object({ type: z.literal('session_activity'), expiresAt: z.number().int().positive() })
        .strict()
        .safeParse(value);
      if (ready && activity.success) {
        session.expiresAt = activity.data.expiresAt;
        return;
      }
      if (!ready) {
        z.object({ type: z.literal('ready'), expiresAt: z.number() }).parse(value);
        ready = true;
        clearTimeout(startup);
        resolveReady();
        return;
      }
      const response = z
        .object({ type: z.literal('nyx_result'), response: responseSchema })
        .strict()
        .parse(value).response;
      if (!pending || response.id !== pending.id) throw new Error('Unexpected response.');
      const request = pending;
      pending = null;
      clearTimeout(request.timer);
      request.resolve(response);
    } catch {
      fail();
      socket.close();
    }
  };
  try {
    await opened;
  } catch (error) {
    clearTimeout(startup);
    socket.close();
    void closeLease(session.sessionId).catch(() => undefined);
    throw error;
  }
  return {
    isOpen: () => ready && socket.readyState === WebSocket.OPEN && Date.now() < session.expiresAt,
    request: async (input) => {
      const request = requestSchema.parse(input);
      if (!ready || socket.readyState !== WebSocket.OPEN || pending || !freshRequest(request))
        throw new Error('Nyx connection unavailable.');
      return new Promise((resolve, reject) => {
        pending = {
          id: request.id,
          resolve,
          reject,
          timer: setTimeout(() => {
            fail();
            socket.close();
          }, 27000),
        };
        try {
          socket.send(JSON.stringify({ type: 'nyx', request }));
        } catch {
          fail();
          socket.close();
        }
      });
    },
    close: async () => {
      clearTimeout(startup);
      fail();
      socket.close();
      await closeLease(session.sessionId).catch(() => undefined);
    },
  };
}
